import React, { useRef, useEffect, useState } from 'react';
import PhilosophySection from './sections/PhilosophySection';
import PerformanceSection from './sections/PerformanceSection';
import BusinessSection from './sections/BusinessSection';
import ContactSection from './sections/ContactSection';
import HeroSection from './parts/HeroSection';
import MessageSection from './sections/MessageSection';
import NewsSection from './sections/NewsSection';
import RecruitSection from './sections/RecruitSection';
import TableOfContents from './parts/TableOfContents';
import Footer from './parts/Footer';
import Header from './parts/Header';

// カスタムフック：スクロールで本文をフェードイン
function useRevealOnScroll(ref) {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const onScroll = () => {
            if (!ref.current) return;
            const rect = ref.current.getBoundingClientRect();
            if (rect.top < window.innerHeight * 0.8) setVisible(true);
        };
        window.addEventListener('scroll', onScroll);
        onScroll();
        return () => window.removeEventListener('scroll', onScroll);
    }, [ref]);
    return visible;
}

function MainPage() {
    const contentRef = useRef(null);
    const mainRef = useRef(null);
    const visible = useRevealOnScroll(contentRef);
    const [cursor, setCursor] = useState({ x: 0, y: 0, active: false });
    const [overlayOpacity, setOverlayOpacity] = useState(1);


    // カスタムカーソルの動き
    useEffect(() => {
        const move = (e) => setCursor(c => ({ ...c, x: e.clientX, y: e.clientY }));
        const down = () => setCursor(c => ({ ...c, active: true }));
        const up = () => setCursor(c => ({ ...c, active: false }));
        window.addEventListener('mousemove', move);
        window.addEventListener('mousedown', down);
        window.addEventListener('mouseup', up);
        return () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mousedown', down);
            window.removeEventListener('mouseup', up);
        };
    }, []);

    // bodyのデフォルトカーソルを消す
    useEffect(() => {
        document.body.style.cursor = 'none';
        return () => { document.body.style.cursor = ''; };
    }, []);


    // スクロールでオーバーレイをフェードアウト
    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            // 最大300pxスクロールでフェードアウト（調整可能）
            const newOpacity = Math.max(0, 1 - scrollY / 2000);
            setOverlayOpacity(newOpacity);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);


    // パーティクル風エフェクト
    useEffect(() => {
        const canvas = document.getElementById('particle-bg');
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let particles = [];
        const w = window.innerWidth;
        const h = window.innerHeight;
        canvas.width = w;
        canvas.height = h;
        for (let i = 0; i < 60; i++) {
            particles.push({
                x: Math.random() * w,
                y: Math.random() * h,
                r: Math.random() * 1.5 + 0.8, // 粒を大きめに
                dx: (Math.random() - 0.5) * 0.5,
                dy: (Math.random() - 0.5) * 0.5,
                c: `hsla(${Math.random() * 360},80%,60%,0.7)`
            });
        }
        let anim;
        function draw() {
            ctx.clearRect(0, 0, w, h);
            for (let p of particles) {
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, 2 * Math.PI);
                ctx.fillStyle = p.c;
                ctx.shadowColor = p.c;
                ctx.shadowBlur = 8;
                ctx.fill();
                p.x += p.dx;
                p.y += p.dy;
                if (p.x < 0) p.x = w;
                if (p.x > w) p.x = 0;
                if (p.y < 0) p.y = h;
                if (p.y > h) p.y = 0;
            }
            anim = requestAnimationFrame(draw);
        }
        draw();
        return () => cancelAnimationFrame(anim);
    }, []);

    // スクロールダウンボタンで本文へ
    const handleScroll = () => {
        if (contentRef.current) {
            const y = contentRef.current.getBoundingClientRect().top + window.pageYOffset - 80; // ←ここで-80px上に
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    return (
        <div className="relative min-h-screen text-white font-zen antialiased overflow-x-hidden">
            <div
                className="absolute inset-0 bg-gradient-to-b from-white to-white transition-opacity duration-1000"
                style={{ opacity: overlayOpacity }}
            ></div>
            {/* カスタムカーソル */}
            <div
                style={{
                    position: 'fixed',
                    left: cursor.x,
                    top: cursor.y,
                    pointerEvents: 'none',
                    zIndex: 9999,
                    transform: 'translate(-50%, -50%)',
                    transition: cursor.active
                        ? 'transform 0.08s cubic-bezier(.4,0,.2,1), box-shadow 0.2s'
                        : 'transform 0.18s cubic-bezier(.4,0,.2,1), box-shadow 0.3s',
                }}
            >
                <div
                    className={`custom-cursor ${cursor.active ? 'active' : ''}`}
                />
            </div>
            {/* カスタムカーソル用スタイル */}
            <style>{`
    .custom-cursor {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 2px solid #fb923c; /* orange-400 */
        background: radial-gradient(circle at 30% 30%, #fdba7455 0%, #fbbf2455 80%, transparent 100%);
        box-shadow: 0 0 16px 4px #fdba7455, 0 0 0 2px #fbbf24aa inset;
        transition: background 0.2s, border 0.2s, box-shadow 0.3s;
        mix-blend-mode: lighten;
        backdrop-filter: blur(2px);
    }
    .custom-cursor.active {
        background: radial-gradient(circle at 60% 60%, #fdba7499 0%, #fbbf24cc 80%, transparent 100%);
        border-color: #fdba74;
        box-shadow: 0 0 32px 8px #fdba7499, 0 0 0 4px #fbbf24cc inset;
        transform: scale(1.3);
    }
    @media (max-width: 768px) {
        .custom-cursor { display: none; }
    }
    * { cursor: none !important; }
    
    /* 横スクロール防止 */
    body {
        overflow-x: hidden;
        max-width: 100vw;
    }
    html {
        overflow-x: hidden;
    }
`}</style>
            {/* 背景動画 */}
            <video
                className="fixed top-0 left-0 w-full h-full object-cover -z-20"
                src={`${process.env.PUBLIC_URL}/video/cityhunter.mp4`}
                autoPlay
                loop
                muted
                playsInline
            />
            <div className="fixed top-0 left-0 w-full h-full bg-gray-800/50 pointer-events-none -z-10" />

            {/* パーティクルエフェクト */}
            {/* <canvas
                id="particle-bg"
                className="fixed top-0 left-0 w-full h-full pointer-events-none z-0"
                style={{ mixBlendMode: 'screen' }}
            /> */}
            {/* 近未来グラデーションオーバーレイ */}
            {/* <div className="fixed top-0 left-0 w-full h-full bg-gradient-to-br from-black/10 via-blue-900/20 to-purple-900/20 -z-10 pointer-events-none" /> */}

            {/* ヒーローセクション */}
            <HeroSection handleScroll={handleScroll} />
            {/* ヘッダー */}
            <Header />
            {/* 本文コンテンツ */}
            <div
                ref={contentRef}
                className={`relative z-10 w-full px-0 py-12 transition-all duration-1000 overflow-x-hidden ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
                    }`}
            >
                {/* 目次 */}
                {/* <div className="mt-[36vh] mb-[24vh]">
                    <TableOfContents />
                </div> */}

                {/* 各セクション */}

                <main ref={mainRef} className="space-y-56 overflow-x-hidden">
                    <div className="relative left-1/2 right-1/2 w-screen -translate-x-1/2 overflow-x-hidden">
                        <PhilosophySection />
                    </div>
                    <PerformanceSection />
                    <div className="relative left-1/2 right-1/2 w-screen -translate-x-1/2 overflow-x-hidden">
                        <MessageSection />
                    </div>
                    <RecruitSection />
                    <BusinessSection />
                    <NewsSection />
                    <ContactSection />
                </main>

            </div>
            {/* フッター */}
            <Footer />
            {/* アニメーション用カスタムCSS */}
            <style>{`
            @keyframes fade-in-up {
            0% { opacity: 0; transform: translateY(40px);}
            100% { opacity: 1; transform: translateY(0);}
            }
            .animate-fade-in-up {
            animation: fade-in-up 1s cubic-bezier(.4,0,.2,1) both;
            animation-delay: 0.6s;
            }
            .animate-fade-in {
            animation: fade-in 1.2s cubic-bezier(.4,0,.2,1) both;
            animation-delay: 0.8s;
            }
            @keyframes fade-in {
            0% { opacity: 0;}
            100% { opacity: 1;}
            }
            `}</style>
        </div>
    );
}

export default MainPage;